// ─── safeguards.js — Defensive utilities for CashHorizon ─────────────────────
// Prevents crashes from: bad data shapes, NaN cascades, division by zero,
// stale closures, XSS in HTML exports, and corrupted localStorage.

// ─── Safe number ─────────────────────────────────────────────────────────────
// Coerces anything to a finite number. NaN, Infinity, null, undefined → fallback.
export function num(val, fallback = 0) {
  if (val === null || val === undefined || val === "") return fallback;
  const n = typeof val === "number" ? val : Number(val);
  return Number.isFinite(n) ? n : fallback;
}

// ─── Safe division ───────────────────────────────────────────────────────────
export function safeDiv(a, b, fallback = 0) {
  const na = num(a), nb = num(b);
  return nb === 0 ? fallback : na / nb;
}

// ─── Safe percentage ─────────────────────────────────────────────────────────
export function safePct(part, total, fallback = 0) {
  return safeDiv(part, total, fallback) * 100;
}

// ─── Safe array ──────────────────────────────────────────────────────────────
export function arr(val) {
  return Array.isArray(val) ? val : [];
}

// ─── Safe object ─────────────────────────────────────────────────────────────
export function obj(val) {
  return val && typeof val === "object" && !Array.isArray(val) ? val : {};
}

// ─── Safe string ─────────────────────────────────────────────────────────────
export function str(val, fallback = "") {
  if (val === null || val === undefined) return fallback;
  return typeof val === "string" ? val : String(val);
}

// ─── Deep get with fallback ──────────────────────────────────────────────────
export function get(object, path, fallback = undefined) {
  if (!object || typeof object !== "object") return fallback;
  const keys = typeof path === "string" ? path.split(".") : arr(path);
  let current = object;
  for (const key of keys) {
    if (current === null || current === undefined || typeof current !== "object") return fallback;
    current = current[key];
  }
  return current === undefined ? fallback : current;
}

// ─── Clamp ───────────────────────────────────────────────────────────────────
export function clamp(value, min, max) {
  const n = num(value);
  return Math.min(Math.max(n, min), max);
}

// ─── Schema validation for loaded data ───────────────────────────────────────
// Returns a sanitized copy with safe defaults for missing/invalid fields.
const DATA_SCHEMA = {
  marketplaces: { type: "array", default: [], itemGuard: (m) => ({
    id: str(m?.id, "mkt_" + Date.now()),
    name: str(m?.name, "Unnamed"),
    canDelete: m?.canDelete !== false,
    pctOfTotal: clamp(num(m?.pctOfTotal), 0, 1),
    payoutDelay: num(m?.payoutDelay, 14),
    products: arr(m?.products).map(p => ({
      id: str(p?.id, "prod_" + Date.now()),
      name: str(p?.name, "Product"),
      pctOfMarket: clamp(num(p?.pctOfMarket), 0, 1),
      payout: num(p?.payout),
      deliveryCost: num(p?.deliveryCost),
      ppcType: str(p?.ppcType, "acos"),
      ppcRate: num(p?.ppcRate),
      refundType: str(p?.refundType, "pct"),
      refundRate: num(p?.refundRate),
      refundAmount: num(p?.refundAmount),
      cogs: {
        productCost: num(p?.cogs?.productCost),
        shipping: num(p?.cogs?.shipping),
        customs: num(p?.cogs?.customs),
        prep: num(p?.cogs?.prep),
      },
      ...p, // preserve any extra fields
    })),
    ...m, // preserve extra fields like icon, color, activeFrom, sharedBrands
  })},
  months: { type: "object", default: {} },
  recurringIncome: { type: "array", default: [] },
  recurringExpenses: { type: "array", default: [] },
  orders: { type: "array", default: [] },
  partners: { type: "array", default: [] },
  modifiers: { type: "array", default: [] },
  decisions: { type: "array", default: [] },
  taxes: { type: "array", default: [] },
  profitCards: { type: "array", default: [] },
  fbmSettings: { type: "array", default: [] },
  visibleYears: { type: "array", default: [] },
  incomeOverrides: { type: "object", default: {} },
  deliveryCreditLimit: { type: "number", default: 0 },
  availableToday: { type: "number", default: 0 },
  unitsSoldThisYear: { type: "number", default: 0 },
  currency: { type: "string", default: "USD" },
  unitSystem: { type: "string", default: "metric" },
  weekStartDay: { type: "number", default: 1 },
};

export function validateData(raw) {
  if (!raw || typeof raw !== "object") return {};
  const result = {};
  for (const [key, schema] of Object.entries(DATA_SCHEMA)) {
    const val = raw[key];
    if (schema.type === "array") {
      const items = arr(val);
      result[key] = schema.itemGuard ? items.map(schema.itemGuard) : items;
    } else if (schema.type === "object") {
      result[key] = obj(val);
    } else if (schema.type === "number") {
      result[key] = val !== undefined ? num(val, schema.default) : undefined;
    } else if (schema.type === "string") {
      result[key] = val !== undefined ? str(val, schema.default) : undefined;
    }
  }
  // Preserve any extra top-level fields not in schema
  for (const key of Object.keys(raw)) {
    if (!(key in result)) result[key] = raw[key];
  }
  return result;
}

// ─── Safe localStorage ───────────────────────────────────────────────────────
export function lsGet(key, fallback = null) {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return raw;
  } catch { return fallback; }
}

export function lsGetJSON(key, fallback = null) {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch { return fallback; }
}

export function lsSet(key, value) {
  try {
    localStorage.setItem(key, typeof value === "string" ? value : JSON.stringify(value));
    return true;
  } catch {
    // Quota exceeded or private mode — silently fail
    return false;
  }
}

// ─── HTML sanitization for PDF/export outputs ────────────────────────────────
const HTML_ESCAPE_MAP = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };

export function escHtml(str) {
  if (typeof str !== "string") return String(str ?? "");
  return str.replace(/[&<>"']/g, c => HTML_ESCAPE_MAP[c] || c);
}

// ─── Safe function executor (wraps any function to never throw) ──────────────
export function safeFn(fn, fallback = undefined, label = "safeFn") {
  return (...args) => {
    try {
      return fn(...args);
    } catch (err) {
      console.warn(`[${label}] caught:`, err?.message || err);
      return typeof fallback === "function" ? fallback(...args) : fallback;
    }
  };
}

// ─── Functional state updater (prevents stale closure overwrites) ────────────
// Usage: setMonths(immutableUpdate(key, patch))
// Instead of: setMonths({...months, [key]: {...months[key], ...patch}})
export function immutableUpdate(key, patch) {
  return prev => ({
    ...prev,
    [key]: { ...(prev[key] || {}), ...(typeof patch === "function" ? patch(prev[key] || {}) : patch) },
  });
}

// Nested version: immutableUpdateNested("key", "subKey", patch)
export function immutableUpdateNested(key, subKey, patch) {
  return prev => ({
    ...prev,
    [key]: {
      ...(prev[key] || {}),
      [subKey]: typeof patch === "function" ? patch((prev[key] || {})[subKey]) : patch,
    },
  });
}
